import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';

import { Container, PageHeader } from '@occc-app/design-system';
import { State as RootState } from '@occc-app/rootRedux';
import { PageableRequest } from '@occc-app/Offer/components/PageableRequest';
import { useChainId } from '@occc-app/Chain/hooks';

import { actions, State as OffersState } from './redux';
import { OffersTable } from './OffersTable';

import { ButtonGroup } from '../Offer/components/ButtonGroup';

export const OffersComponent = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const chainId = useChainId();

  const {
    data: { offers },
    filters,
    requests,
  } = useSelector<RootState, OffersState>(({ offers }) => ({
    ...offers,
  }));

  useEffect(() => {
    if (!chainId) {
      return;
    }
    dispatch(actions.requestOffers(chainId));
  }, [chainId, dispatch]);

  const onFilterChange = useCallback(
    (filterKey: keyof OffersState['filters']) => (ev: React.FormEvent<any>) =>
      dispatch(actions.updateFilter(filterKey, ev.currentTarget.value)),
    [dispatch],
  );

  return (
    <>
      <Helmet>
        <title>Offers</title>
      </Helmet>
      <Container includesPageHeader>
        <PageHeader titleTestId='offers-title' title='Offer Management' />

        <section>
          <PageableRequest
            fetch={() => dispatch(actions.requestOffers(chainId))}
            changePath={(path: string) => dispatch(actions.changePath(path))}
            requestState={requests.offers}
          >
            <PageableRequest.RecordManagement>
              <PageableRequest.Search
                key='filter-search'
                onChange={onFilterChange('search')}
                value={filters.search}
              />

              <ButtonGroup
                key='filter-publication-status'
                label='Publication Status'
                name='publicationStatus'
                onChange={onFilterChange('publicationStatus')}
                selectedValue={filters.publicationStatus}
              >
                <ButtonGroup.Option value='all'>All</ButtonGroup.Option>
                <ButtonGroup.Option value='published'>Active</ButtonGroup.Option>
                <ButtonGroup.Option value='unpublished'>Scheduled</ButtonGroup.Option>
                <ButtonGroup.Option value='expired'>Expired</ButtonGroup.Option>
              </ButtonGroup>

              <ButtonGroup
                key='filter-discontinued'
                label='Archived'
                name='discontinued'
                onChange={onFilterChange('discontinued')}
                selectedValue={filters.discontinued}
              >
                <ButtonGroup.Option value='false'>Active</ButtonGroup.Option>
                <ButtonGroup.Option value='true'>Archived</ButtonGroup.Option>
              </ButtonGroup>
            </PageableRequest.RecordManagement>

            <OffersTable offers={offers} push={navigate} chainId={chainId} />
          </PageableRequest>
        </section>
      </Container>
    </>
  );
};

export { default as offers } from './redux';
